import { useState, useMemo, useEffect, Suspense } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { useQuery } from "@tanstack/react-query";
import { UIButton } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { DialogEbaRetail } from "./dialog-eba-retail";
import { DialogEbaRetailDetail } from "./dialog-eba-retail-detail";
import { useDict } from "../../../app/[lang]/providers/DictionaryProvider";
import { useLocale } from "../../../app/[lang]/providers/LocaleProvider";
import { useNavigationMenu } from "@/hooks/useNavigationMenu";
import { http } from "@/lib/http";
import { homePageQueryKey } from "../../../app/[lang]/homepage/hooks/useHomePage";
import type { Calculator } from "../../../app/[lang]/homepage/type/type";

interface EbaRetailCalculatorProps {
  calculatorTitle?: string;
  calculatorDesc?: string;
  buttonText?: string;
  buttonUrl?: string;
  amountLabel?: string;
  couponLabel?: string;
  submitLabel?: string;
  couponOptions?: Array<{ label: string; value: number | string; is_published?: boolean }>;
}

const DEFAULT_COUPON_OPTIONS = [
  { label: "7%", value: 7 },
  { label: "6.65%", value: 6.65 },
  { label: "6.5%", value: 6.5 },
];

// The backend returns coupon values as strings (e.g. "6.65") and may include
// unpublished options, so normalize before handing them to the calculator.
function normalizeCouponOptions(
  options?: Array<{ label: string; value: number | string; is_published?: boolean }>
): { label: string; value: number }[] {
  if (!options || options.length === 0) return DEFAULT_COUPON_OPTIONS;
  return options
    .filter((opt) => opt.is_published !== false)
    .map((opt) => ({ label: opt.label, value: Number(opt.value) }))
    .filter((opt) => Number.isFinite(opt.value));
}

function EbaRetailCalculatorContent({
  calculatorTitle,
  calculatorDesc,
  buttonText,
  buttonUrl,
  amountLabel,
  couponLabel,
  submitLabel,
  couponOptions: couponOptionsProp,
}: EbaRetailCalculatorProps) {
  const dict = useDict();
  const lang = useLocale();
  const { data: navigationData } = useNavigationMenu();
  const searchParams = useSearchParams();

  // When no content props are supplied (e.g. the calculator_section on the EBA
  // Retail template page), pull the copy from the homepage API.
  const hasContentProps = Boolean(
    calculatorTitle || calculatorDesc || buttonText || buttonUrl ||
    amountLabel || couponLabel || submitLabel || couponOptionsProp?.length
  );
  const { data: homePage } = useQuery<Calculator>({
    queryKey: homePageQueryKey(lang),
    enabled: !hasContentProps,
    queryFn: async () => {
      const res = await http.get(`/api/home-pages/1?lang=${lang}`);
      return res.data.data;
    },
  });

  const content = hasContentProps ? undefined : homePage;
  const resolvedTitle = calculatorTitle ?? content?.calculator_title;
  const resolvedDesc = calculatorDesc ?? content?.calculator_desc;
  const resolvedButtonText = buttonText ?? content?.calculator_button_text;
  const resolvedButtonUrl = buttonUrl ?? content?.calculator_button_url;
  const resolvedAmountLabel = amountLabel ?? content?.calculator_label_amount;
  const resolvedCouponLabel = couponLabel ?? content?.calculator_label_coupon;
  const resolvedSubmitLabel = submitLabel ?? content?.calculator_label_submit;
  const couponOptions = normalizeCouponOptions(couponOptionsProp ?? content?.calculator_coupon_options);
  const [funds, setFunds] = useState(() => searchParams.get("funds") || "");
  const [coupon, setCoupon] = useState<number | null>(() => {
    const param = searchParams.get("coupon");
    return param ? Number(param) : null;
  });
  const [showDialog, setShowDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(() => searchParams.get("popup") === "true");
  const router = useRouter();
  
  // Handle client-side navigation to URL with params
  useEffect(() => {
    const fundsParam = searchParams.get("funds");
    const couponParam = searchParams.get("coupon");
    const popupParam = searchParams.get("popup");
    
    if (fundsParam) setFunds(fundsParam);
    if (couponParam) setCoupon(Number(couponParam));
    
    if (popupParam === "true") {
      setShowDetailDialog(true);
    }
  }, [searchParams]);
  
  // Find the EBA Retail menu item and get its path
  const ebaRetailPath = useMemo(() => {
    if (!navigationData) return null;
    
    const findItemPath = (items: any[], targetSlug: string): string | null => {
      for (const item of items) {
        if (item.slug === targetSlug) {
          return item.path;
        }
        if (item.children && item.children.length > 0) {
          const childPath = findItemPath(item.children, targetSlug);
          if (childPath) return childPath;
        }
      }
      return null;
    };
    
    return findItemPath(navigationData, 'eba-retail');
  }, [navigationData]);
  const handleShowCalculation = () => {
    const popupParam = searchParams.get("popup");
    if (popupParam === "true") {
      setShowDetailDialog(true);
    } else {
      setShowDialog(true);
    }
  };
  const onShowResult = () => {
    setShowDialog(false);
    if (!ebaRetailPath) return;
    
    const params = new URLSearchParams({
      popup: "true",
      funds: funds.toString(),
      coupon: coupon?.toString() || "",
    });
    router.push(`/${lang}${ebaRetailPath}?${params.toString()}`);
  };
  return (
    <div className="max-w-7xl mx-auto">
        <div
        className="relative w-full rounded-2xl overflow-hidden flex flex-col md:flex-row items-stretch min-h-[300px]"
        style={{ background: `url('/images/bg-eba.png') right center/cover no-repeat, linear-gradient(90deg, #083E8A 0%, #FF9900 100%)` }}
        >
        <div className="flex-1 w-full md:max-w-2/5 p-6 md:p-8 flex flex-col justify-center bg-[#083E8A]/90 text-white z-10">
            <h2 className="text-2xl md:text-3xl font-bold mb-2 text-white   ">{resolvedTitle ?? dict.ebaRetail.title}</h2>
            {resolvedDesc
              ? <div className="mb-6" dangerouslySetInnerHTML={{ __html: resolvedDesc }} />
              : <p className="mb-6">{dict.ebaRetail.description}</p>
            }
            <a href={resolvedButtonUrl ?? "https://webnew-dev.smf-indonesia.co.id/en/services/eba-retail"} target="_blank" rel="noopener noreferrer">
              <UIButton variant="outline" className="w-max">{resolvedButtonText ?? dict.ebaRetail.seeWhatsInside}</UIButton>
            </a>
        </div>
        <div className="flex-1 w-full md:max-w-3/5 p-6 md:p-8 flex flex-col justify-center z-10">
            <form
            className="flex flex-col md:flex-row gap-4 items-stretch md:items-end justify-center"
            onSubmit={e => { e.preventDefault(); handleShowCalculation(); }}
            >
            <div className="flex flex-col w-full md:w-auto">
                <label htmlFor="funds" className="mb-1 text-md font-medium  text-gray-700">{resolvedAmountLabel ?? dict.ebaRetail.fundsToInvest}</label>
                <Input
              id="funds"
              type="number"
              placeholder={(resolvedAmountLabel ?? dict.ebaRetail.fundsToInvest) as string}
              value={funds}
              onChange={e => setFunds(e.target.value)}
              required
              min={1}
            />
            </div>
            <div className="flex flex-col w-full md:w-auto">
                <label htmlFor="coupon" className="mb-1 text-md font-medium  text-gray-700">{resolvedCouponLabel ?? dict.ebaRetail.coupon}</label>
                <Select
                  value={coupon ? String(coupon) : ""}
                  onValueChange={val => setCoupon(Number(val))}
                  required
                >
                  <SelectTrigger id="coupon">
                    {coupon ? (couponOptions.find(opt => Number(opt.value) === coupon)?.label ?? String(coupon)) : (resolvedCouponLabel ?? dict.ebaRetail.selectCoupon)}
                  </SelectTrigger>
                  <SelectContent>
                    {couponOptions.map(opt => (
                      <SelectItem key={opt.value} value={String(opt.value)}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
            </div>
            <div className="flex flex-col w-full md:w-auto">
              <UIButton type="submit" className="bg-[#083E8A] text-white px-6 py-2 rounded-md">{resolvedSubmitLabel ?? dict.ebaRetail.showCalculation}</UIButton>
            </div>
            </form>
        </div>
        <DialogEbaRetail
            open={showDialog}
            onOpenChange={setShowDialog}
            funds={funds}
            coupon={coupon}
            onShowResult={onShowResult}
        />
        <DialogEbaRetailDetail
            open={showDetailDialog}
            onOpenChange={setShowDetailDialog}
            funds={Number(funds) || 0}
            coupon={coupon}
            onClose={() => setShowDetailDialog(false)}
        />
        <div className="absolute inset-0 z-0" />
        </div>
    </div>
  );
}

export default function EbaRetailCalculator(props: EbaRetailCalculatorProps) {
  return (
    <Suspense fallback={<div className="max-w-7xl mx-auto min-h-[300px]" />}>
      <EbaRetailCalculatorContent {...props} />
    </Suspense>
  );
}
